import {
  authSessionKey,
  authSessionQueryOptions,
  replaceAuthSession
} from './queries/session';
import React, {
  createContext,
  use,
  ReactNode,
  Suspense,
  useState,
  useCallback,
  useLayoutEffect
} from 'react';
import { Resources, RhinoRegisteredResource, RhinoResource } from './index';
import {
  QueryClient,
  QueryClientProvider,
  useSuspenseQuery
} from '@tanstack/react-query';
import { setBaseOwnerHeader, setPageViewHeaderSource } from './lib/networking';
import modelLoader from './models';
import { getModuleInfo } from './utils/models';
import {
  RhinoDevtoolsEventClient,
  defaultDevtoolsEventClient
} from './hooks/devtoolsEventClient';
import {
  RhinoAnalyticsClient,
  defaultAnalyticsClient
} from './analytics/client';

export type SessionUserRoles = {
  id: number;
  role: { name: string };
  organization: { id: number; [key: string]: unknown };
};

export type SessionUser = {
  id: number;
  name: string | null;
  nickname: string | null;
  image: string | null;
  email: string | null;
  display_name: string | null;
  users_roles?: SessionUserRoles[];
};

export type SessionResponse = {
  data: {
    success: boolean;
    data: SessionUser | null;
  };
};

// FIXME the user and owner types need to be better defined
export interface RhinoContextType {
  user: RhinoRegisteredResource<'user'> | null;
  baseOwner: { id: number; [key: string]: unknown } | null;
  setBaseOwner: (
    baseOwner:
      SessionResponse['data']['data'] | SessionUserRoles['organization'] | null
  ) => void;
  /**
   * Re-fetch the session and sync the user, base owner, and roles in place,
   * preserving the currently selected organization. Call this after mutating
   * the current user or the base owner — e.g. editing the profile or the
   * organization — so their names and logos refresh without a full reload.
   */
  refreshSession: () => Promise<void>;
  usersRoles: SessionUserRoles[];
  logIn: (user: SessionUser) => void;
  logOut: () => void;
  resources: Record<keyof Resources, RhinoResource>;
  queryClient: QueryClient;
  devtoolsEventClient: RhinoDevtoolsEventClient;
  analyticsClient: RhinoAnalyticsClient;
}
interface RhinoProviderProps {
  children: ReactNode;
  queryClient?: QueryClient;
  devtoolsEventClient?: RhinoDevtoolsEventClient;
  analyticsClient?: RhinoAnalyticsClient;
  /**
   * Returns the id of the organization to select as the base owner when a
   * session is established (restore or login) — typically the host app's
   * persisted "last used" organization. Ids are compared as strings, so a
   * value read straight from storage works. When it returns no id, or an id
   * the user has no role in, selection falls back to the first role's
   * organization. Supplying this avoids a first render (and its queries)
   * against an organization the host immediately switches away from.
   */
  initialBaseOwnerId?: () => string | number | null | undefined;
}

const defaultQueryClient = new QueryClient({});

export const RhinoContext = createContext<RhinoContextType>({
  baseOwner: null,
  setBaseOwner: undefined!,
  refreshSession: async () => {},
  usersRoles: [],
  logIn: () => {},
  logOut: () => {},
  resources: {} as Record<keyof Resources, RhinoResource>,
  user: null,
  queryClient: defaultQueryClient,
  devtoolsEventClient: defaultDevtoolsEventClient,
  analyticsClient: defaultAnalyticsClient
});

export const useRhinoContext = () => use(RhinoContext);

// Same as RhinoContext but with the queryClient required
export type RhinoContentProps = RhinoProviderProps &
  Required<
    Pick<
      RhinoProviderProps,
      'queryClient' | 'devtoolsEventClient' | 'analyticsClient'
    >
  >;

const RhinoContent: React.FC<RhinoContentProps> = ({
  children,
  initialBaseOwnerId,
  ...props
}) => {
  const { queryClient } = props;

  const [user, setUser] = useState<SessionUser | null>(null);
  const [baseOwner, setBaseOwner] = useState<
    SessionUser | SessionUserRoles['organization'] | null
  >(null);
  const [usersRoles, setUsersRoles] = useState<SessionUserRoles[]>([]);

  const resources = modelLoader.resources as RhinoContextType['resources'];

  // Org-backed = base owner differs from auth owner.
  const rhino = getModuleInfo('rhino');
  const isOrganization = !!rhino && rhino.baseOwner !== rhino.authOwner;

  // Select the base owner for a newly established session: the host's
  // preferred organization when it resolves to one of the user's roles,
  // otherwise the first role's organization.
  const pickInitialBaseOwner = useCallback(
    (roles: SessionUserRoles[]) => {
      const preferredId = initialBaseOwnerId?.();
      const preferred =
        preferredId != null
          ? roles.find(
              (role) => String(role.organization.id) === String(preferredId)
            )
          : undefined;

      return preferred?.organization ?? roles[0]?.organization ?? null;
    },
    [initialBaseOwnerId]
  );

  // Check for an active session
  const { data, refetch } = useSuspenseQuery(
    authSessionQueryOptions({ suppressSessionErrors: true })
  );

  // Requests are made from plain modules with no access to this state, so the
  // organization being worked in and the current page view are handed to the
  // networking layer here, where they are owned.
  const { analyticsClient } = props;
  useLayoutEffect(() => {
    setBaseOwnerHeader(baseOwner?.id);
    analyticsClient.setBaseOwner(baseOwner?.id);
  }, [analyticsClient, baseOwner?.id]);
  useLayoutEffect(() => {
    setPageViewHeaderSource(() => analyticsClient.currentPageViewId);
    return () => setPageViewHeaderSource(undefined);
  }, [analyticsClient]);

  // Enforce the user is set correctly before a render
  // https://github.com/TanStack/router/blob/d372e99dd8a6eaeb65df63836170fd70aa2f09af/examples/react/kitchen-sink-file-based/src/routes/login.tsx#L27
  useLayoutEffect(() => {
    // If we have a user and we are not already set
    // FIXME: Update if the user info changes?  ie not deep equal
    if (data && !user) {
      const newUser = data;
      // eslint-disable-next-line @eslint-react/set-state-in-effect, react-hooks/set-state-in-effect
      setUser(newUser);
      if (isOrganization) {
        const usersRoles = newUser?.users_roles ?? [];

        // FIXME: Where to handle no roles?
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setBaseOwner(pickInitialBaseOwner(usersRoles));
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setUsersRoles(usersRoles);
      } else {
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setBaseOwner(newUser);
        // eslint-disable-next-line @eslint-react/set-state-in-effect
        setUsersRoles([]);
      }
      // No session, or the check failed, so clear everything
    } else if (!data) {
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setUser(null);
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setBaseOwner(null);
      // eslint-disable-next-line @eslint-react/set-state-in-effect
      setUsersRoles([]);
    }
  }, [data, isOrganization, user, pickInitialBaseOwner]);

  const logIn = useCallback(
    async (user: SessionUser) => {
      setUser(user);
      if (isOrganization) {
        const usersRoles = user?.users_roles ?? [];

        // FIXME: Where to handle no roles?
        setBaseOwner(pickInitialBaseOwner(usersRoles));
        setUsersRoles(usersRoles);
      } else {
        setBaseOwner(user);
        setUsersRoles([]);
      }

      // Ensure the session validation is up to date
      await refetch();
    },
    [isOrganization, refetch, pickInitialBaseOwner]
  );

  const logOut = useCallback(async () => {
    // The sign-out hook already replaced the session; direct callers still
    // need the shared account-cache cleanup.
    if (queryClient.getQueryData(authSessionKey) !== null)
      await replaceAuthSession(queryClient, null);
    setUser(null);
    setBaseOwner(null);
    setUsersRoles([]);
    analyticsClient.reset();
  }, [analyticsClient, queryClient]);

  const refreshSession = useCallback(async () => {
    const { data: fresh } = await refetch();
    const freshUser = fresh;
    if (!freshUser) return;

    setUser(freshUser);
    if (isOrganization) {
      const roles = freshUser.users_roles ?? [];
      setUsersRoles(roles);
      // Keep the org the user currently has selected, refreshed with its latest
      // data, rather than snapping back to the first role.
      setBaseOwner(
        (prev) =>
          roles.find((r) => r.organization.id === prev?.id)?.organization ??
          roles[0]?.organization ??
          null
      );
    } else {
      setBaseOwner(freshUser);
    }
  }, [refetch, isOrganization]);

  return (
    <RhinoContext
      value={{
        resources,
        baseOwner,
        setBaseOwner,
        refreshSession,
        usersRoles,
        user: user as RhinoContextType['user'],
        logOut,
        logIn,
        ...props
      }}
    >
      {children}
    </RhinoContext>
  );
};

export const RhinoProvider: React.FC<RhinoProviderProps> = ({
  queryClient = defaultQueryClient,
  devtoolsEventClient = defaultDevtoolsEventClient,
  analyticsClient = defaultAnalyticsClient,
  ...props
}) => {
  return (
    <Suspense fallback={null}>
      <QueryClientProvider client={queryClient}>
        <RhinoContent
          queryClient={queryClient}
          devtoolsEventClient={devtoolsEventClient}
          analyticsClient={analyticsClient}
          {...props}
        />
      </QueryClientProvider>
    </Suspense>
  );
};
